import { Button, DrawerProps, Table } from 'antd';
import { antdDrawer, useModal, create } from '@ebay/nice-modal-react';
import { CustomDrawer } from '@isocel/front-common';
import moment from 'moment';

import { PackageInventoryItem } from '../../queries/api/types';
import './InventoryHistory.less';

interface InventoryDetailsData {
    date: string;
    storeName: string;
    storeUserName: string;
    packagesInventory: PackageInventoryItem[];
    packageLabelById: Map<string, string>;
    packageCodeById: Map<string, string>;
}

type Props = DrawerProps & { data: InventoryDetailsData };

const InventoryDetailsDrawer = create<Props>(({ data, ...props }) => {
    const modal = useModal();

    const handleClose = () => {
        modal.hide();
    };

    const formattedDate = data.date
        ? new Date(data.date).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          })
        : '';

    const sortedPackages = [...data.packagesInventory].sort((a, b) => {
        const dateA = a.inventoryDate || '';
        const dateB = b.inventoryDate || '';
        const dateCmp = dateA.localeCompare(dateB);
        if (dateCmp !== 0) return dateCmp;
        const codeA = data.packageCodeById.get(a.package) || '';
        const codeB = data.packageCodeById.get(b.package) || '';
        return codeA.localeCompare(codeB, undefined, { numeric: true });
    });

    const columns = [
        {
            title: 'Date & Heure',
            dataIndex: 'inventoryDate',
            key: 'inventoryDate',
            render: (value: string) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : ''),
        },
        {
            title: 'Code emballage',
            dataIndex: 'package',
            key: 'code',
            render: (value: string) => data.packageCodeById.get(value) || value,
        },
        {
            title: 'Emballage',
            dataIndex: 'package',
            key: 'package',
            render: (value: string) => data.packageLabelById.get(value) || value,
        },
        {
            title: 'Quantité comptée',
            dataIndex: 'countedValue',
            key: 'countedValue',
        },
        {
            title: 'Écart constaté',
            dataIndex: 'actualDifference',
            key: 'actualDifference',
            render: (value: number) => {
                const cls = value === 0 ? 'ecart-zero' : value > 0 ? 'ecart-positive' : 'ecart-negative';
                return <span className={cls}>{value > 0 ? `+${value}` : value}</span>;
            },
        },
    ];

    return (
        <CustomDrawer
            width={700}
            title={`Inventaire du ${formattedDate}${data.storeName ? ` - ${data.storeName}` : ''}`}
            {...props}
            {...antdDrawer(modal)}
            onClose={handleClose}
        >
            <div>
                <div className="flex justify-between context" style={{ marginBottom: 16 }}>
                    <div className="info-item">
                        <b>Date :</b> {formattedDate}
                    </div>
                    {data.storeName && (
                        <div className="info-item">
                            <b>Magasin :</b> {data.storeName}
                        </div>
                    )}
                    <div className="info-item">
                        <b>Utilisateur :</b> {data.storeUserName}
                    </div>
                    <div className="info-item">
                        <b>Nb emballages :</b> {data.packagesInventory.length}
                    </div>
                </div>

                <h3>Détail des emballages inventoriés :</h3>
                <Table
                    columns={columns}
                    dataSource={sortedPackages}
                    rowKey={(item, idx) => `${item.package}-${String(idx)}`}
                    pagination={false}
                    size="small"
                />

                <div style={{ marginTop: 16 }}>
                    <Button type="primary" onClick={handleClose}>
                        Retour
                    </Button>
                </div>
            </div>
        </CustomDrawer>
    );
});

export default InventoryDetailsDrawer;
